import { ContextMenuOption } from '../../../models/context-menu-option';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint } from '../../../models/enums';
import { NavigationInfo } from '../../../models/navigation-info';
import { ActionType, ObjectActionListType, PermissionSet, Permissions } from '../../../models/permission';
import { Role } from '../../../models/role';
import { Template, TemplateType } from '../../../models/template';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';

const templatesContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Save to Template ...',
        iconClass: 'fa-save', iconColor: 'color-blue-01',
        visible: (item: GenericTreeNode) => (item.data as Template).templateType !== TemplateType.META_TEMPLATE_PROFILE,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Load from Template ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (item: GenericTreeNode) => (item.data as Template).templateType !== TemplateType.CUSTOM_FIELDS && (item.data as Template).templateType !== TemplateType.META_TEMPLATE_PROFILE,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        divider: true,
        visible: (item: GenericTreeNode) => (item.data as Template).templateType === TemplateType.EXPORT_FIELDS || (item.data as Template).templateType === TemplateType.DOCUMENT_METADATA,
    }),
    new ContextMenuOption({
        name: 'Download as XML ...', iconClass: 'fa-download', iconColor: 'color-blue-01',
        visible: (item: GenericTreeNode) => (item.data as Template).templateType === TemplateType.EXPORT_FIELDS || (item.data as Template).templateType === TemplateType.DOCUMENT_METADATA,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Load from XML ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (item: GenericTreeNode) => (item.data as Template).templateType === TemplateType.EXPORT_FIELDS || (item.data as Template).templateType === TemplateType.DOCUMENT_METADATA,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })],
        tooltip: 'A new template will be created from the XML file'
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Edit ...',
        iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        enabled: (item: GenericTreeNode) => !(item.data as Template).defaultTemplate,
        permissions: [new PermissionSet({ permissionActions: [ActionType.DELETE] })]
    })
];

const templateHeaderButtons: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'New Template ...',
        iconClass: 'fa-plus', iconColor: 'color-green-04',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
];

const headerContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Download All as XMLs ...', iconClass: 'fa-download', iconColor: 'color-blue-01',
        visible: (item: GenericTreeNode) => item.requestInfo.data.tempType === TemplateType.EXPORT_FIELDS || item.requestInfo.data.tempType === TemplateType.DOCUMENT_METADATA,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Load from XMLs ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (item: GenericTreeNode) => item.requestInfo.data.tempType === TemplateType.EXPORT_FIELDS || item.requestInfo.data.tempType === TemplateType.DOCUMENT_METADATA,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })],
        tooltip: 'A new template will be created from each XML file'
    })
];

const rolePermissionHeaderButtons: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'New Role ...', iconClass: 'fa-plus', iconColor: 'color-green-04',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    })
];

const rolePermissionContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Set as Default', iconClass: 'fa-check', iconColor: 'color-green-04',
        enabled: (item: GenericTreeNode) => ((item.data as Role).attributes[0].value === 'false'),
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_ROLE, permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        enabled: (item: GenericTreeNode) => !((item.data as Role).name === 'System Administrator'),
        visible: (item: GenericTreeNode) => !((item.data as Role).name === 'System Administrator'),
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_ROLE, permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Copy ...', iconClass: 'fa-copy', iconColor: 'color-grey-10',
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_ROLE, permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        enabled: (item: GenericTreeNode) => !((item.data as Role).name === 'System Administrator') && ((item.data as Role).attributes[0].value === 'false'),
        visible: (item: GenericTreeNode) => !((item.data as Role).name === 'System Administrator'),
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_ROLE, permissionActions: [ActionType.DELETE] })]
    })
];

export const SystemMenu: Array<GenericTreeNode> = [
    // TODO: uncomment when it's decided to support also this functionality
    // new GenericTreeNode({
    //     name: 'Membership',
    //     automationId: 'system-settings-tree-membership',
    //     isExpandable: true,
    //     isExpanded: true,
    //     isExpandedOnSelect: true,
    //     isSelectable: false,
    //     hideExpander: true,

    //     children: [
    //         new GenericTreeNode({
    //             name: 'Users',
    //             automationId: 'system-settings-tree-membership-users',
    //             icon: 'fa-user',
    //             iconColor: 'color-purple-03',
    //             childPadding: 5,
    //             navigationInfo: new NavigationInfo({ name: 'Users', path: 'settings-empty' }),
    //         }),
    //         new GenericTreeNode({
    //             name: 'Groups',
    //             automationId: 'system-settings-tree-membership-groups',
    //             icon: 'fa-users',
    //             iconColor: 'color-purple-03',
    //             childPadding: 5,
    //             navigationInfo: new NavigationInfo({ name: 'Groups', path: 'settings-empty' }),
    //         }),
    //     ]
    // }),
    new GenericTreeNode({
        name: 'Configuration',
        automationId: 'system-settings-configuration',
        isExpandable: true,
        isExpanded: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        hideExpander: true,

        children: [
            new GenericTreeNode({
                name: 'Password & User Logout Policy',
                automationId: 'system-settings-password-and-user-logout-policy-configuration',
                icon: 'fa-key',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-password-and-user-logout-policy', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PASSWORD_AND_USER_LOGOUT_POLICY })]
            }),
            new GenericTreeNode({
                name: 'Email Server & Notifications',
                automationId: 'system-settings-email-server-and-notifications-configuration',
                icon: 'fa-envelope',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-email-server', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.EMAIL_SERVER_AND_NOTIFICATIONS })]
            }),
            new GenericTreeNode({
                name: 'System Storage Depot',
                automationId: 'system-settings-system-storage-depot-configuration',
                icon: 'fa-dropbox',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-system-storage-depot', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_STORAGE })]
            }),
            new GenericTreeNode({
                name: 'Review System',
                automationId: 'system-settings-review-system-configuration',
                icon: 'fa-clone',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-system-review-system', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.REEF_REVIEW_CONNECTOR })]
            }),
            new GenericTreeNode({
                name: 'Virus Detection',
                automationId: 'system-settings-virus-detection-configuration',
                icon: 'fa-bug',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-virus-detection', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.VIRUS_DETECTION })]
            }),
            new GenericTreeNode({
                name: 'System Message',
                automationId: 'system-settings-system-message-configuration',
                icon: 'fa-info-circle',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-system-message', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_MESSAGE })]
            })
        ]
    }),
    new GenericTreeNode({
        name: 'Provisioning',
        automationId: 'system-settings-general',
        isExpandable: true,
        isExpanded: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        hideExpander: true,

        children: [
            new GenericTreeNode({
                name: 'License',
                automationId: 'system-settings-license-provisioning',
                icon: 'fa-certificate',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-license', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.LICENSE })]
            }),
            new GenericTreeNode({
                name: 'Realm Nodes',
                automationId: 'system-settings-realm-nodes-provisioning',
                icon: 'fa-puzzle-piece',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-realm-nodes', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.REALM_NODES })]
            }),
            new GenericTreeNode({
                name: 'Storage',
                automationId: 'system-settings-storage',
                icon: 'fa-dropbox',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-system-storage', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.STORAGE })],
            }),
            new GenericTreeNode({
                name: 'Services',
                automationId: 'system-settings-services',
                icon: 'fa-cloud',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-services', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SERVICES })],
                customClasses: 'node-border-bottom',
            }),
            new GenericTreeNode({
                name: 'Organizations',
                automationId: 'system-settings-organizations',
                icon: 'fa-bank',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-organizations', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORGANIZATIONS })],
            }),
            new GenericTreeNode({
                name: 'Projects',
                automationId: 'system-settings-projects-provisioning',
                icon: 'fa-book',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-projects', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PROJECTS })],
            }),
            new GenericTreeNode({
                name: 'Delete Pending Projects',
                automationId: 'system-settings-delete-pending-projects-provisioning',
                icon: 'fa-book',
                iconColor: 'color-yellow-07',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-delete-pending-projects', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.DELETE_PENDING_PROJECTS })],
            }),
            new GenericTreeNode({
                name: 'Deleted Projects',
                automationId: 'system-settings-deleted-projects-provisioning',
                icon: 'fa-book',
                iconColor: 'color-grey-08',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-deleted-projects', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.DELETED_PROJECTS })]
            }),
            new GenericTreeNode({
                name: 'System Role Permissions',
                automationId: 'system-settings-tree-general-roles',
                icon: 'fa-user-md',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: rolePermissionHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.realmManager_listSystemRoles,
                    responseName: 'roles',
                    data: { objectType: 'ALL', returnObjectActionLists: false, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_ROLE })],
                createChild: (data: any): GenericTreeNode => {
                    const role = new Role(data);
                    let isDefaultRole = 'false';
                    if (role && role.attributes) {
                        const itemIndex = role.attributes.findIndex(item => item.name === 'defaultRole');
                        if (itemIndex >= 0) {
                            isDefaultRole = role.attributes[itemIndex].value;
                        }
                    }
                    role.isSystemRole = true;
                    return new GenericTreeNode({
                        name: role.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-system-role-permissions' }),
                        customText: isDefaultRole === 'true' ? 'Default' : '',
                        icon: 'fa-user-md',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: true,
                        contextMenuOptions: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_ROLE, ActionType.CREATE) ? rolePermissionContextMenuOptions : null,
                        data: role
                    });
                }
            }),
            new GenericTreeNode({
                name: 'System Users',
                automationId: 'system-settings-system-users',
                icon: 'fa-user',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-users-permission', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_USER })]
            }),
            new GenericTreeNode({
                name: 'System Groups',
                automationId: 'system-settings-system-groups',
                icon: 'fa-users',
                iconColor: 'color-purple-03',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-groups', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_GROUP })]
            })
        ]
    }),
    new GenericTreeNode({
        name: 'Templates',
        automationId: 'system-settings-tree-templates',
        isExpandable: true,
        isExpanded: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        hideExpander: true,

        children: [
            new GenericTreeNode({
                name: 'Template Profiles',
                automationId: 'system-settings-tree-templates-profiles',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                iconThird: 'fa-check',
                iconThirdColor: 'white',
                childPadding: 5,
                customClasses: 'node-border-bottom',
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.META_TEMPLATE_PROFILE, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_META_TEMPLATE })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ path: 'settings-template-profiles' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        iconSecond: 'template-profile-check fa-check',
                        iconSecondColor: 'white',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_META_TEMPLATE, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_META_TEMPLATE })],
                        data: item
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Analytic Settings',
                automationId: 'system-settings-tree-templates-analytic-settings',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.ANALYTICAL_SETTINGS, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_ANALYTIC_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ path: 'settings-analytic-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_ANALYTIC_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_ANALYTIC_SETTINGS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Billing Reports',
                automationId: 'system-settings-tree-templates-billing-reports',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.BILLING_REPORT_SETTINGS, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_BILLING_REPORTS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-billing-report' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_BILLING_REPORTS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_BILLING_REPORTS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Domain Lists',
                automationId: 'system-settings-tree-templates-domain-lists',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.DOMAIN_LISTS, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_DOMAIN_LISTS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-domain-lists' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_DOMAIN_LISTS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_DOMAIN_LISTS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Excluded Content',
                automationId: 'system-settings-tree-templates-excluded-content',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.EMAIL_SIGNATURE, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_EXCLUDED_CONTENT })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: 'Shared Data Sets', path: 'settings-excluded-content' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_EXCLUDED_CONTENT, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_EXCLUDED_CONTENT })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Export Fields',
                automationId: 'system-settings-tree-templates-export-fields',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.EXPORT_FIELDS, systemScope: true }
                }),
                contextMenuOptions: headerContextMenuOptions,
                showContextMenuButton: true,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_EXPORT_FIELDS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-export-fields' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_EXPORT_FIELDS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_EXPORT_FIELDS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Export Settings',
                automationId: 'org-settings-tree-templates-export-settings',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.EXPORT_SETTINGS, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_EXPORT_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-export-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_EXPORT_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_EXPORT_SETTINGS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Index Settings',
                automationId: 'system-settings-tree-templates-index-settings',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.INDEX_SETTINGS, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_INDEX_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-index-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_INDEX_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_INDEX_SETTINGS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Metadata Search Fields',
                automationId: 'system-settings-tree-templates-metadata-search-fields',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.SEARCH_FIELDS, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_METADATA_SEARCH_FIELDS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-metadata-fields' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_METADATA_SEARCH_FIELDS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_METADATA_SEARCH_FIELDS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Metadata View Fields',
                automationId: 'system-settings-tree-templates-metadata-view-fields',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.DOCUMENT_METADATA, systemScope: true }
                }),
                contextMenuOptions: headerContextMenuOptions,
                showContextMenuButton: true,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_METADATA_VIEW_FIELDS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-metadata-fields' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_METADATA_VIEW_FIELDS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_METADATA_VIEW_FIELDS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Patterns',
                automationId: 'system-settings-tree-templates-patterns',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.USER_EXP, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_PATTERNS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-patterns' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_PATTERNS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_PATTERNS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Reports',
                automationId: 'system-settings-tree-templates-reports',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.REPORT_SETTINGS, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_REPORTS_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-report-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_REPORTS_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_REPORTS_SETTINGS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Search Settings',
                automationId: 'system-settings-tree-templates-search-settings',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.SEARCH_SETTINGS, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_SEARCH_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-search-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_SEARCH_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_SEARCH_SETTINGS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Survivorship Model',
                automationId: 'system-settings-tree-templates-survivorship-model',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.DUPE_SURVIVORSHIP, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_SURVIVORSHIP_MODEL_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-survivorship' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_SURVIVORSHIP_MODEL_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_SURVIVORSHIP_MODEL_SETTINGS })],
                        data: item,
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Tags',
                automationId: 'system-settings-tree-templates-tags',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-purple-03',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listTemplates,
                    responseName: 'templates',
                    data: { scope: 'SYSTEM_LEVEL', tempType: TemplateType.TAG, systemScope: true }
                }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_TAG_SETTINGS })],
                createChild: (data: any): GenericTreeNode => {
                    const item = new Template(data);
                    return new GenericTreeNode({
                        name: item.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-tags-settings' }),
                        customText: item.defaultTemplate ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-purple-03',
                        childPadding: 5,
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.SYSTEM_TAG_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.SYSTEM_TAG_SETTINGS })],
                        data: item,
                    });
                },
            })
        ]
    })

];
